import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { TABLE_DATE } from "consts";

import { EFilterDateValue, UrlQueryParams } from "types/common";

dayjs.extend(utc);
dayjs.extend(timezone);

export const convertToTitleCase = (str: string): string => {
  const valueUpperCase = `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
  return valueUpperCase.replace(/_/g, " ");
};

export const isMatch = (arg1: unknown, arg2: unknown) =>
  JSON.stringify(arg1) === JSON.stringify(arg2);

export const convertStatusToText = (status: string) => {
  if (!status) return null;
  return status.replaceAll("_", " ");
};

export const generateRequestUrl = (
  url: string,
  queryParams?: UrlQueryParams,
): string => {
  if (queryParams) {
    const queryString = Object.entries(queryParams)
      .map(([key, value]) => {
        //for current BE pagination
        if (key === "offset") {
          return `${key}=${encodeURIComponent(
            (
              ((+value as number) - 1) *
              (queryParams?.limit as number)
            ).toString(),
          )}`;
        }
        return value
          ? `${key}=${encodeURIComponent(value.toString())}`
          : undefined;
      })
      .filter(Boolean)
      .join("&");

    return `${url}?${queryString}`;
  }

  return url;
};

export const dateTransform = ({
  date,
  format = TABLE_DATE,
}: {
  date: string | Date | dayjs.Dayjs | null;
  format?: string;
}) => {
  if (!date) {
    return null;
  }
  return dayjs(date).format(format);
};

export const dateCreate = (
  date?: string | Date | dayjs.Dayjs,
  isUTCDisabled?: boolean,
): dayjs.Dayjs => {
  if (date === null) {
    return;
  }
  if (!date) {
    return isUTCDisabled
      ? dayjs(Date.now()).local()
      : dayjs.utc(Date.now()).local();
  }
  return isUTCDisabled ? dayjs(date).local() : dayjs.utc(date).local();
};

export const dateTransformToRequestFormat = (
  date: dayjs.Dayjs,
  utc?: boolean,
) => {
  return utc ? dayjs.utc(date).toISOString() : date.toISOString();
};

export const getRageDate = (value: EFilterDateValue | string) => {
  switch (value) {
    case EFilterDateValue.TODAY:
      return {
        pickupTimeBefore: dayjs().startOf("day").toISOString(),
        pickupTimeAfter: dayjs().endOf("day").toISOString(),
      };
    case EFilterDateValue.YESTERDAY:
      return {
        pickupTimeBefore: dateCreate()
          .subtract(1, "day")
          .startOf("day")
          .toISOString(),
        pickupTimeAfter: dateCreate()
          .subtract(1, "day")
          .endOf("day")
          .toISOString(),
      };
    case EFilterDateValue.IN_THE_LAST_7_DAYS:
      return {
        pickupTimeBefore: dayjs()
          .subtract(8, "day")
          .startOf("day")
          .toISOString(),
        pickupTimeAfter: dayjs().subtract(1, "day").endOf("day").toISOString(),
      };
    case EFilterDateValue.IN_THE_LAST_30_DAYS:
      return {
        pickupTimeBefore: dayjs()
          .subtract(31, "day")
          .startOf("day")
          .toISOString(),
        pickupTimeAfter: dayjs().subtract(1, "day").endOf("day").toISOString(),
      };
    case EFilterDateValue.IN_THE_LAST_90_DAYS:
      return {
        pickupTimeBefore: dayjs()
          .subtract(91, "day")
          .startOf("day")
          .toISOString(),
        pickupTimeAfter: dayjs().subtract(1, "day").endOf("day").toISOString(),
      };
    case EFilterDateValue.IN_THE_12_MONTHS:
      return {
        pickupTimeBefore: dayjs()
          .subtract(12, "month")
          .subtract(1, "day")
          .startOf("day")
          .toISOString(),
        pickupTimeAfter: dayjs().subtract(1, "day").endOf("day").toISOString(),
      };
    default:
      return {
        pickupTimeBefore: dayjs().startOf("day").toISOString(),
        pickupTimeAfter: dayjs().endOf("day").toISOString(),
      };
  }
};

export const getFullName = ({
  firstName,
  lastName,
}: {
  firstName: string | null;
  lastName: string | null;
}): string | null => [firstName, lastName].filter(Boolean).join(" ") || null;

export const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) {
    return str;
  } else {
    return str.slice(0, maxLength) + "...";
  }
};
